import { useState, useEffect } from "react";
import { api } from "@/App";
import { AlertTriangle, X, Check, Eye, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function AlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlerts();
    // Poll toutes les 30 secondes
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get("/alerts");
      setAlerts(response.data);
    } catch (error) {
      console.error("Erreur chargement alertes");
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      toast.success("Alerte résolue");
      fetchAlerts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const unreadCount = alerts.filter(a => !a.read).length;

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 p-4 rounded-full shadow-lg z-50 transition-all ${
          unreadCount > 0 
            ? "bg-red-600 hover:bg-red-700 animate-pulse" 
            : "bg-slate-700 hover:bg-slate-600"
        }`}
        data-testid="alerts-toggle"
      >
        <AlertTriangle className="h-6 w-6 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="fixed right-0 top-0 h-screen w-96 bg-white shadow-2xl z-50 flex flex-col animate-slide-in">
          <div className="p-4 border-b flex items-center justify-between bg-red-600 text-white">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="font-bold">Dossiers Bloqués</h2>
              {alerts.length > 0 && (
                <Badge className="bg-white text-red-600">{alerts.length}</Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-white hover:bg-red-700">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {alerts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Check className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                <p className="font-semibold">Aucune alerte</p>
                <p className="text-sm">Tous les dossiers sont en ordre</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border ${
                    alert.read ? "bg-slate-50 border-slate-200" : "bg-red-50 border-red-200"
                  }`}
                  data-testid={`alert-${alert.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {alert.vehicle && (
                        <p className="font-mono font-bold text-slate-900">
                          {alert.vehicle.immatriculation}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        Par {alert.user_name} • {new Date(alert.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigate(`/vehicles/${alert.vehicle_id}`);
                        setIsOpen(false);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => resolveAlert(alert.id)}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Résoudre
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
